import { useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import AdminShell from "../../components/AdminShell";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/stats").then((r) => setStats(r.data));
  }, []);

  return (
    <AdminShell>
      <div className="brand-tag">// Overview</div>
      <h1 className="font-display text-5xl uppercase tracking-tight mt-1">Control Room</h1>

      {/* Hero stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-1 mt-10">
        <Stat label="Live Now" value={stats?.live_now ?? "—"} accent />
        <Stat label="Total Lives" value={stats?.total_lives ?? "—"} />
        <Stat label="Total Bookings" value={stats?.total_bookings ?? "—"} />
        <Stat label="Revenue (₹)" value={stats?.total_revenue?.toLocaleString("en-IN") ?? "—"} />
      </div>

      {/* Section: Catalog */}
      <SectionHeader num="02" title="Catalog Flow" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-1 mt-3">
        <Stat label="Discover" value={stats?.discover_count ?? "—"} />
        <Stat label="In Vault" value={stats?.vault_count ?? "—"} />
        <Stat label="Promoted" value={stats?.promoted_count ?? "—"} />
        <Stat label="Auto-Promoted Lives" value={stats?.auto_promoted_lives ?? "—"} accent />
      </div>

      {/* Section: Inventory */}
      <SectionHeader num="03" title="Inventory Health" />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-1 mt-3">
        <Stat label="Total Products" value={stats?.total_products ?? "—"} />
        <Stat
          label="Low Stock (≤10)"
          value={stats?.low_stock ?? "—"}
          accent={(stats?.low_stock ?? 0) > 0}
        />
        <Stat
          label="Sold Out"
          value={stats?.out_of_stock ?? "—"}
          accent={(stats?.out_of_stock ?? 0) > 0}
        />
      </div>

      {/* Section: Community */}
      <SectionHeader num="04" title="Community" />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-1 mt-3">
        <Stat label="Total Users" value={stats?.total_users ?? "—"} />
        <Stat label="Scheduled Lives" value={stats?.scheduled_lives ?? "—"} />
        <Stat label="Past Lives" value={stats?.ended_lives ?? "—"} />
      </div>

      <div className="mt-12 border border-[#E5E1D8] bg-[#FFFFFF] p-6">
        <div className="brand-tag mb-2">// Quick Actions</div>
        <h2 className="font-display text-2xl uppercase tracking-tight">Operational Playbook</h2>
        <ol className="mt-4 space-y-2 font-mono-ds text-sm text-[#6E6E6E]">
          <li>01 // Add products in <strong className="text-[#161616]">Inventory</strong> → set Track (Discover/Vault) + Category</li>
          <li>02 // Drops auto-promote from Vault @ 100 requests → review them under <strong className="text-[#161616]">Lives</strong></li>
          <li>03 // 9 PM IST: host the live, mark milestones, watch bookings stream in</li>
          <li>04 // Fulfill orders under <strong className="text-[#161616]">Bookings</strong> — update status from Confirmed → Shipped → Delivered</li>
          <li>05 // Monitor buyer growth + LTV under <strong className="text-[#161616]">Users</strong></li>
        </ol>
      </div>
    </AdminShell>
  );
}

function SectionHeader({ num, title }) {
  return (
    <div className="mt-10 mb-1 flex items-end justify-between border-b border-[#E5E1D8] pb-2">
      <div>
        <div className="brand-tag">// {num}</div>
        <h2 className="font-display text-2xl uppercase tracking-tight mt-1">{title}</h2>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div
      className={`border ${accent ? "border-[#FF6A00] shadow-[0_4px_12px_rgba(17,17,17,0.06)]" : "border-[#E5E1D8]"} bg-[#FFFFFF] p-5`}
      data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
    >
      <div className="font-mono-ds text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E]">{label}</div>
      <div className={`font-display text-3xl md:text-4xl font-bold mt-2 ${accent ? "text-[#FF6A00]" : "text-[#161616]"}`}>{value}</div>
    </div>
  );
}
